import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Pager from "@/components/site/Pager";
import { api } from "@/lib/api";
import { toast } from "sonner";
import useDragReorder from "@/lib/useDragReorder";

const PAGE_SIZE = 25;

export default function AdminJobs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/jobs", { params: { q: q || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, page]);
  useEffect(() => { setPage(1); }, [q]);

  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const canReorder = !q && page === 1;   // Only reorder the visible page & unfiltered list.
  const drag = useDragReorder({
    items,
    getId: (j) => j.id,
    onReorder: async (newList, ids) => {
      setItems(newList);
      try { await api.post("/admin/jobs/reorder", { ids }); toast.success("Order updated"); }
      catch { toast.error("Failed to save order"); load(); }
    },
  });

  return (
    <div className="space-y-6" data-testid="admin-jobs">
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Jobs</h1>
          <p className="text-sm text-slate-500">{total} role{total === 1 ? "" : "s"}{canReorder ? " · drag to reorder" : ""}</p>
        </div>
        <div className="flex w-full items-center gap-2 md:w-auto">
          <Input placeholder="Search title or location…" value={q} onChange={(e) => setQ(e.target.value)} className="md:w-64" data-testid="admin-jobs-search" />
          <Link to="/admin/jobs/new"><Button className="bg-primary hover:bg-primary/90" data-testid="admin-jobs-new"><Plus className="mr-1 h-4 w-4" />New Job</Button></Link>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {canReorder && <th className="w-10 px-2 py-3"></th>}
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Applications</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((j) => {
              const rowExtras = canReorder ? drag.rowProps(j) : {};
              const rowCls = canReorder ? drag.rowClass(j) : "";
              return (
                <tr key={j.id} {...rowExtras} className={`border-b last:border-0 transition-colors ${rowCls || "hover:bg-slate-50"}`} data-testid={`admin-job-row-${j.id}`}>
                  {canReorder && (
                    <td className="w-10 px-2 py-3 text-center">
                      <drag.Handle testId={`admin-job-row-${j.id}-handle`} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{j.title}</div>
                    <div className="text-xs text-slate-500">{j.department}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{j.location}</td>
                  <td className="px-4 py-3 text-slate-700">{j.employment_type}</td>
                  <td className="px-4 py-3 text-slate-700">{j.application_count || 0}</td>
                  <td className="px-4 py-3">
                    <Badge className={j.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>
                      {j.is_active ? "Active" : "Closed"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Link to={`/jobs/${j.slug}`} target="_blank"><Button variant="ghost" size="sm"><ExternalLink className="h-3.5 w-3.5" /></Button></Link>
                      <Link to={`/admin/jobs/${j.id}`}><Button variant="ghost" size="sm" data-testid={`admin-job-edit-${j.id}`}><Edit2 className="h-3.5 w-3.5" /></Button></Link>
                      <Button variant="ghost" size="sm" onClick={() => remove(j.id)} data-testid={`admin-job-delete-${j.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && items.length === 0 && (
              <tr><td colSpan={canReorder ? 7 : 6} className="px-4 py-10 text-center text-slate-400">No jobs yet. Click &quot;New Job&quot; to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-jobs-pager" />
    </div>
  );
}
